import { Component, inject, signal, viewChild } from '@angular/core';
import { disabled, form, FormField, required } from '@angular/forms/signals';
import { TranslatePipe } from '@ngx-translate/core';
import { FilterBuilder } from '@libs/shared/utils/grid.builder';
import { ServiceGroupEnum } from '@libs/types/src/services/enum/service-group.enum';
import { ServiceGroupTypeEnum } from '@libs/types/src/services/enum/service-group-type.enum';
import { SocialServiceOperationEnum } from '@libs/types/src/services/enum/social-service-operation.enum';
import { SocialCreditGroupSettings } from '@libs/types/src/services/types/social-credit-group-settings.types';
import {
    SocialServiceOfferGroupObject,
} from '@libs/types/src/services/object-type/service/social-service/social-service-offer-group.object';
import { FolderGroupObject } from '@libs/types/src/services/object-type/folder/folder-group.object';
import { FolderGroupUpdateInput } from '@libs/types/src/services/input-type/folder/folder-group.update-input';
import { BaseDialogComponent } from '../../../kit/base-dialog/base-dialog.component';
import { BaseDialogService } from '../../../kit/base-dialog/base-dialog-service';
import { TextInputComponent } from '../../../kit/input/text-input-v2/text-input.component';
import { TextAreaInputComponent } from '../../../kit/input/text-area-input-v2/text-area-input.component';
import { NumberInputComponent } from '../../../kit/input/text-input-v2/number-input.component';
import { DateInputComponent } from '../../../kit/input/date-input-v2/date-input.component';
import { CheckInputComponent } from '../../../kit/input/check-input-v2/check-input.component';
import { ColorInputComponent } from '../../../kit/input/color-input-v2/color-input.component';
import { ActionButtonsComponent } from '../../../kit/action-buttons/action-buttons.component';
import { CardTableComponent } from '../../../../modules/shared/components/kit/table/card-table.component';
import {
    TableColumn, TableColumnType, TableDeleteEvent,
} from '../../../../modules/shared/components/kit/table/table.types';
import { userChipField } from '../../../../modules/shared/components/kit/table/table.fields';
import { UserChipRenderTypeEnum } from '../../../kit/user-chip/user-chip.component.types';
import { ConfirmBuilder } from '../../../../modules/shared/service/confirm.builder';
import { translate } from '../../../../modules/shared/shared.global-service';
import { checkOk } from '../../../../modules/content/content.error';
import { dateify } from '../../../../modules/shared/helpers/date.helper';
import { SocialCreditGroupDialogStore } from './social-credit-group-dialog.store';
import { SocialCreditGroupDialogData, SocialCreditGroupForm } from './social-credit-group-dialog.types';
import { creditGroupMembersFragment, creditGroupOffersFragment } from './social-credit-group-dialog.query';
import {
    ServiceGroupUpsertOffersDialogComponent,
} from '../../../../modules/content/components/services/settings/service-group-upsert-offers-dialog/service-group-upsert-offers-dialog.component';
import {
    ServiceGroupUpsertClientsDialogComponent,
} from '../../../../modules/content/components/services/settings/service-group-upsert-clients-dialog/service-group-upsert-clients-dialog.component';
import {
    ServiceGroupEditItemValidityDialogComponent,
} from '../../../../modules/content/components/services/settings/service-group-edit-offer-dialog/service-group-edit-item-validity-dialog.component';

@Component({
    selector: 'app-social-credit-group-dialog',
    templateUrl: './social-credit-group-dialog.component.html',
    styleUrl: './social-credit-group-dialog.component.scss',
    providers: [SocialCreditGroupDialogStore],
    imports: [FormField, TranslatePipe, TextInputComponent, TextAreaInputComponent, NumberInputComponent,
        DateInputComponent, CheckInputComponent, ColorInputComponent, CardTableComponent, ActionButtonsComponent],
})
export class SocialCreditGroupDialogComponent extends BaseDialogComponent {
    protected readonly store = inject(SocialCreditGroupDialogStore);
    protected readonly dialogs = inject(BaseDialogService);
    protected readonly confirm = inject(ConfirmBuilder);

    protected readonly offersTable = viewChild<CardTableComponent>('offersTable');
    protected readonly membersTable = viewChild<CardTableComponent>('membersTable');

    protected readonly data = this.dialogConfig.data as SocialCreditGroupDialogData;
    protected readonly settings: Partial<SocialCreditGroupSettings> =
        JSON.parse(this.data.serviceGroup?.settings || '{}');
    protected readonly model = signal<SocialCreditGroupForm>({
        name: this.data.serviceGroup?.name ?? '',
        description: this.data.serviceGroup?.description ?? '',
        color: this.data.serviceGroup?.color ?? '#ff0000',
        active: this.data.serviceGroup?.active ?? true,
        validFrom: this.data.serviceGroup ? dateify(this.data.serviceGroup.validFrom) : new Date(),
        validTo: dateify(this.data.serviceGroup?.validTo),
        creditAmount: this.settings.creditAmount ?? null,
        creditValidityDays: this.settings.creditValidityDays ?? null,
    });
    protected readonly fields = form(this.model, schema => {
        disabled(schema, () => this.store.loadingAny());
        required(schema.name);
        required(schema.validFrom);
        required(schema.creditAmount);
        required(schema.creditValidityDays);
    });
    protected readonly filters = new FilterBuilder({
        serviceId: this.data.serviceId,
        groupId: this.data.serviceGroup?.id || 0,
    }).filter().filter;
    protected readonly offerColumns: TableColumn[] = [
        { name: 'code', dataKey: 'offer.code', filterable: false, sortable: false },
        { name: 'name', dataKey: 'offer.name', filterable: false, sortable: false },
        { name: 'unit', dataKey: 'offer.unit', filterable: false, sortable: false,
            data: (unit: string) => translate.instant('enum.measurementUnit.' + unit) },
        { name: 'creditPrice', filterable: { type: 'number' } },
        { name: 'validFrom', type: TableColumnType.Date, filterable: { type: 'date' } },
        { name: 'validTo', type: TableColumnType.Date, filterable: { type: 'date' } },
    ];
    protected readonly memberColumns: TableColumn[] = [
        userChipField('client', UserChipRenderTypeEnum.Client, {
            dataKey: 'folder.person', filterable: false, sortable: false,
        }),
        { name: 'validFrom', type: TableColumnType.Date, filterable: { type: 'date' } },
        { name: 'validTo', type: TableColumnType.Date, filterable: { type: 'date' } },
    ];

    // TODO: filter is the opposite of what is billed, confirm
    private readonly nonBillableOffersFilter = new FilterBuilder()
        .where('type', [SocialServiceOperationEnum.BILL, SocialServiceOperationEnum.FACULTATIVE], 'NIN')
        .orWhere('unitPrice', 0, 'LTE')
        .orWhere('unitPrice', '', 'EMPTY')
        .where('type', [SocialServiceOperationEnum.BILL, SocialServiceOperationEnum.FACULTATIVE], 'NIN')
        .orWhere('unitPriceExt', 0, 'LTE')
        .orWhere('unitPriceExt', '', 'EMPTY')
        .filter().filter!;

    public override async save(): Promise<void> {
        if (this.store.loadingAny() || this.fields().invalid()) return;
        const value = this.model();
        const settings: SocialCreditGroupSettings = {
            creditAmount: value.creditAmount!,
            creditValidityDays: value.creditValidityDays!,
        };
        const data = {
            name: value.name,
            description: value.description,
            color: value.color,
            active: value.active,
            validFrom: value.validFrom?.toISOString() ?? null,
            validTo: value.validTo?.toISOString() ?? null,
            settings: JSON.stringify(settings),
        };
        const result = this.data.serviceGroup
            ? await this.store.api.updateServiceGroup({ id: this.data.serviceGroup.id, ...data })
            : await this.store.api.createServiceGroup({
                serviceId: Number(this.data.serviceId),
                type: ServiceGroupEnum.CREDITS,
                groupType: ServiceGroupTypeEnum.GROUP,
                ...data,
            });
        if (checkOk(result, this.data.serviceGroup ? 'update' : 'create')) this.dialogRef.close({ refresh: true });
    }

    protected editOffer(item: SocialServiceOfferGroupObject | null = null): void {
        const ref = this.dialogs.open(ServiceGroupUpsertOffersDialogComponent, {
            header: translate.instant('socialCredits.groups.offers.' + (item ? 'edit' : 'create')),
            styleClass: 'p-dialog-width-30',
            data: {
                serviceId: this.data.serviceId,
                groupId: this.data.serviceGroup!.id,
                groupType: ServiceGroupEnum.CREDITS,
                item,
                additionalFilter: this.nonBillableOffersFilter,
            },
        });
        this.subs.push(ref.onClose.subscribe(result => { if (result?.refresh) this.offersTable()?.reload(); }));
    }

    protected editMember(item: FolderGroupObject | null = null): void {
        const component = item ? ServiceGroupEditItemValidityDialogComponent : ServiceGroupUpsertClientsDialogComponent;
        const ref = this.dialogs.open(component, {
            header: translate.instant('socialCredits.groups.members.' + (item ? 'edit' : 'create')),
            styleClass: 'p-dialog-width-30',
            data: item ? {
                item,
                promise: (input: FolderGroupUpdateInput) => this.store.api.updateFolderGroup({
                    id: input.id,
                    validFrom: input.validFrom ?? null,
                    validTo: input.validTo ?? null,
                }),
            } : {
                serviceId: this.data.serviceId,
                groupId: this.data.serviceGroup!.id,
                creditGroup: true,
            },
        });
        this.subs.push(ref.onClose.subscribe(result => { if (result?.refresh) this.membersTable()?.reload(); }));
    }

    protected deleteOffer(event: TableDeleteEvent<SocialServiceOfferGroupObject>): void {
        this.confirm.confirmDelete(async () => {
            const result = await this.store.api.deleteSocialServiceOfferGroup({ id: event.item.id });
            if (checkOk(result, 'delete')) this.offersTable()?.reload();
        }, event);
    }

    protected deleteMember(event: TableDeleteEvent<FolderGroupObject>): void {
        this.confirm.confirmDelete(async () => {
            const result = await this.store.api.deleteFolderGroup({ id: event.item.id });
            if (checkOk(result, 'delete')) this.membersTable()?.reload();
        }, event);
    }

    protected readonly creditGroupOffersFragment = creditGroupOffersFragment;
    protected readonly creditGroupMembersFragment = creditGroupMembersFragment;
}
